import { useState, useRef, useMemo, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm, useFieldArray, useWatch, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { 
  ChevronLeft, 
  Plus, 
  Trash2, 
  Loader2, 
  FileText,
  Search,
  Receipt,
  User,
  Calendar,
  Printer,
  CheckCircle,
  Tag,
  Layers,
  ShoppingBag,
  Package,
  Sparkles,
  Building2,
  CreditCard,
  ChevronDown,
  ChevronUp,
  X,
  UserCheck,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useToast } from '@/hooks/useToast';
import { useDataCache } from '@/hooks/useDataCache';

interface ProductDiscount {
  id: number;
  name: string;
  code: string | null;
  type: 'PERCENTAGE' | 'FIXED';
  value: string;
  maxAmount: string | null;
  isActive: boolean;
}

interface Product {
  id: number;
  sku: string;
  name: string;
  price: string;
  taxRate: string;
  type: string;
  isActive: boolean;
  defaultDiscountId?: number | null;
  defaultDiscount?: ProductDiscount | null;
}

interface Customer {
  id: number;
  numeroIdentificacionUnico: string;
  nombre: string;
  apellidoPaterno: string;
  apellidoMaterno: string;
  email: string;
}

interface Discount {
  id: number;
  name: string;
  code: string | null;
  type: 'PERCENTAGE' | 'FIXED';
  value: string;
  maxAmount: string | null;
  isActive: boolean;
  validFrom: string | null;
  validTo: string | null;
  isExpired?: boolean;
  isNotYetValid?: boolean;
}

const invoiceItemSchema = z.object({
  productId: z.number().min(1, 'Seleccione un producto'),
  quantity: z.number().min(0.01, 'La cantidad debe ser mayor a 0'),
  unitPrice: z.number().min(0, 'El precio debe ser mayor o igual a 0'),
  taxRate: z.number().min(0).max(1),
  description: z.string().optional(),
  discountId: z.number().nullable().optional(),
  discountPct: z.number().min(0).max(100).optional(),
});

const invoiceSchema = z.object({
  customerId: z.number().min(1, 'Seleccione un cliente'),
  date: z.string().min(1, 'La fecha es requerida'),
  dueDate: z.string().min(1, 'La fecha de vencimiento es requerida'),
  notes: z.string().optional(),
  items: z.array(invoiceItemSchema).min(1, 'Debe agregar al menos un producto'),
});

type InvoiceForm = z.infer<typeof invoiceSchema>;

interface TaxProfile {
  id: number;
  profileName: string;
  rfc: string;
  legalName: string;
  taxRegimeCode: string;
  zipCode: string;
  fiscalAddress?: string | null;
  emailForInvoices: string;
  defaultUsoCfdi: string;
  isDefault: boolean;
}

interface TaxRegime {
  code: string;
  name: string;
}

const TAX_REGIMES: TaxRegime[] = [
  { code: '601', name: 'General de Ley Personas Morales' },
  { code: '603', name: 'Personas Morales con Fines no Lucrativos' },
  { code: '605', name: 'Sueldos y Salarios e Ingresos Asimilados a Salarios' },
  { code: '606', name: 'Arrendamiento' },
  { code: '607', name: 'Régimen de Enajenación o Adquisición de Bienes' },
  { code: '608', name: 'Demás ingresos' },
  { code: '610', name: 'Residentes en el Extranjero sin Establecimiento Permanente en México' },
  { code: '611', name: 'Ingresos por Dividendos (socios y accionistas)' },
  { code: '612', name: 'Personas Físicas con Actividades Empresariales y Profesionales' },
  { code: '614', name: 'Ingresos por intereses' },
  { code: '616', name: 'Sin obligaciones fiscales' },
  { code: '621', name: 'Incorporación Fiscal' },
  { code: '625', name: 'Actividades Empresariales con ingresos a través de Plataformas Tecnológicas' },
  { code: '626', name: 'Régimen Simplificado de Confianza' },
];

interface ActivePromotion {
  id: number;
  name: string;
  code: string | null;
  type: 'escalonada' | 'volumen' | 'forma_pago' | 'paquete';
  discountType: 'PERCENTAGE' | 'FIXED';
  discountValue: string;
  maxDiscountAmount: string | null;
  stackable: boolean;
  startDate: string | null;
  endDate: string | null;
  minQuantity: string | null;
  tiers: any[] | null;
  products: { productId: number; productName: string; sku: string }[];
}

async function fetchProducts(): Promise<Product[]> {
  const response = await fetch('/api/sales/products', { credentials: 'include' });
  if (!response.ok) throw new Error('Error al cargar productos');
  return response.json();
}

async function fetchActivePromotions(): Promise<ActivePromotion[]> {
  const response = await fetch('/api/promotions/active', { credentials: 'include' });
  if (!response.ok) return [];
  return response.json();
}

interface PromoPreviewItem {
  productId: number;
  quantity: number;
  unitPrice: number;
  baseAmount: number;
  promotionDiscountAmount: number;
  applicable: boolean;
}

interface PromoPreview {
  promotionId: number;
  promotionName: string;
  promotionCode: string | null;
  type: string;
  applicable: boolean;
  reason: string | null;
  items: PromoPreviewItem[];
  totalDiscount: number;
}

async function fetchPromotionPreview(promotionId: number, items: { productId: number; quantity: number; unitPrice: number }[]): Promise<PromoPreview | null> {
  const r = await fetch('/api/promotions/preview', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ promotionId, items }),
  });
  if (!r.ok) return null;
  return r.json();
}

async function fetchCustomers(): Promise<Customer[]> {
  const response = await fetch('/api/alumnos?limit=9999&page=1', { credentials: 'include' });
  if (!response.ok) throw new Error('Error al cargar clientes');
  const data = await response.json();
  return Array.isArray(data) ? data : (data.alumnos || []);
}

async function createInvoice(data: InvoiceForm & { autoIssue: boolean; promoPreview?: PromoPreview | null; promoAmountsPerItem?: number[]; taxProfileId?: number | null }) {
  const { promoPreview, promoAmountsPerItem } = data;

  const response = await fetch('/api/sales/invoices', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({
      customerId: data.customerId,
      date: data.date,
      dueDate: data.dueDate,
      notes: data.notes || undefined,
      autoIssue: data.autoIssue,
      taxProfileId: data.taxProfileId ?? null,
      items: data.items.map((item, idx) => {
        const promoAmt = promoAmountsPerItem?.[idx] ?? 0;
        return {
          productId: item.productId,
          quantity: item.quantity,
          unitPrice: item.unitPrice,
          description: item.description || undefined,
          discountId: item.discountId ?? null,
          discountPct: item.discountId ? undefined : (item.discountPct ?? 0),
          promotionId: (promoPreview?.applicable && promoAmt > 0) ? promoPreview.promotionId : null,
          promotionDiscountAmount: promoAmt,
        };
      }),
    }),
  });
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || error.error || 'Error al crear la factura');
  }
  return response.json();
}

function computeLineDiscount(item: any, discounts: Discount[]): number {
  const baseAmount = (item.quantity || 0) * (item.unitPrice || 0);
  if (!baseAmount) return 0;
  if (item.discountId) {
    const d = discounts.find(x => x.id === item.discountId);
    if (d) {
      if (d.type === 'PERCENTAGE') {
        const pct = parseFloat(d.value);
        let amt = baseAmount * pct / 100;
        if (d.maxAmount) amt = Math.min(amt, parseFloat(d.maxAmount));
        return amt;
      } else {
        return Math.min(parseFloat(d.value), baseAmount);
      }
    }
  }
  if (item.discountPct && item.discountPct > 0) {
    return baseAmount * item.discountPct / 100;
  }
  return 0;
}

function TotalsFooter({ control, discounts, promoDiscByFormIdx }: { control: any; discounts: Discount[]; promoDiscByFormIdx: Map<number, number> }) {
  const items = useWatch({ control, name: 'items' });

  const { subtotal, discountTotal, taxTotal, grandTotal, taxBreakdown, promoSavings } = useMemo(() => {
    let sub = 0;
    let disc = 0;
    let tax = 0;
    let promo = 0;
    const taxes: Record<string, number> = {};

    (items || []).forEach((item: any, idx: number) => {
      if (item.productId && item.quantity > 0 && item.unitPrice >= 0) {
        const base = item.quantity * item.unitPrice;
        const discAmt = computeLineDiscount(item, discounts);
        const promoDisc = promoDiscByFormIdx.get(idx) ?? 0;
        const lineSubtotal = base - discAmt - promoDisc;
        const lineTax = lineSubtotal * (item.taxRate || 0);
        sub += lineSubtotal;
        disc += discAmt;
        promo += promoDisc;
        tax += lineTax;
        if (item.taxRate > 0) {
          const taxLabel = `IVA ${(item.taxRate * 100).toFixed(0)}%`;
          taxes[taxLabel] = (taxes[taxLabel] || 0) + lineTax;
        }
      }
    });

    return { subtotal: sub, discountTotal: disc, taxTotal: tax, grandTotal: sub + tax, taxBreakdown: taxes, promoSavings: promo };
  }, [items, discounts, promoDiscByFormIdx]);

  const fmt = (n: number) => `$${n.toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  return (
    <div className="border-t bg-gray-50 p-4">
      <div className="flex flex-col sm:flex-row items-start sm:items-end justify-between gap-4">
        <div className="text-sm text-gray-500">
          {Object.keys(taxBreakdown).length > 0 && (
            <div className="space-y-1">
              {Object.entries(taxBreakdown).map(([label, amount]) => (
                <div key={label} className="flex justify-between gap-8">
                  <span>{label}:</span>
                  <span className="font-mono">{fmt(amount)}</span>
                </div>
              ))}
            </div>
          )}
        </div>
        <div className="flex flex-col items-end gap-2">
          <div className="flex items-center gap-8 text-sm">
            <span className="text-gray-500">Subtotal bruto:</span>
            <span className="font-mono font-medium">{fmt(subtotal + discountTotal + promoSavings)}</span>
          </div>
          {discountTotal > 0 && (
            <div className="flex items-center gap-8 text-sm">
              <span className="text-emerald-600 flex items-center gap-1"><Tag className="w-3 h-3" />Descuentos:</span>
              <span className="font-mono font-medium text-emerald-600">−{fmt(discountTotal)}</span>
            </div>
          )}
          {promoSavings > 0 && (
            <div className="flex items-center gap-8 text-sm">
              <span className="text-violet-600 flex items-center gap-1"><Sparkles className="w-3 h-3" />Promoción:</span>
              <span className="font-mono font-medium text-violet-600">−{fmt(promoSavings)}</span>
            </div>
          )}
          <div className="flex items-center gap-8 text-sm">
            <span className="text-gray-500">Subtotal neto:</span>
            <span className="font-mono font-medium">{fmt(subtotal)}</span>
          </div>
          <div className="flex items-center gap-8 text-sm">
            <span className="text-gray-500">IVA Total:</span>
            <span className="font-mono font-medium text-orange-600">{fmt(taxTotal)}</span>
          </div>
          <div className="flex items-center gap-8 text-lg font-bold border-t pt-2 mt-1">
            <span className="text-gray-700">Gran Total:</span>
            <span className="font-mono text-emerald-600">{fmt(grandTotal)}</span>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function NewInvoice() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { cachedData } = useDataCache();
  const [productSearch, setProductSearch] = useState<Record<number, string>>({});
  const [customerSearch, setCustomerSearch] = useState('');
  const [createdInvoice, setCreatedInvoice] = useState<any>(null);
  const [selectedPromoId, setSelectedPromoId] = useState<number | null>(null);
  const [promoPreview, setPromoPreview] = useState<PromoPreview | null>(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [userClearedPromo, setUserClearedPromo] = useState(false);
  const [autoDetected, setAutoDetected] = useState(false);
  const [promoDiscByFormIdx, setPromoDiscByFormIdx] = useState<Map<number, number>>(new Map());
  const formSnapshotRef = useRef<{ formData: InvoiceForm; customer: Customer | undefined } | null>(null);

  // Datos fiscales / receptor
  const [taxProfiles, setTaxProfiles] = useState<TaxProfile[]>([]);
  const [loadingProfiles, setLoadingProfiles] = useState(false);
  const [selectedTaxProfileId, setSelectedTaxProfileId] = useState<number | null>(null);
  const [showNewProfileForm, setShowNewProfileForm] = useState(false);
  const [savingProfile, setSavingProfile] = useState(false);
  const emptyNewProfile = { profileName: '', rfc: '', legalName: '', taxRegimeCode: '', zipCode: '', fiscalAddress: '', emailForInvoices: '', defaultUsoCfdi: 'D10', isDefault: false };
  const [newProfileForm, setNewProfileForm] = useState(emptyNewProfile);

  const { data: products = [], isLoading: loadingProducts } = useQuery({
    queryKey: ['sales-products'],
    queryFn: fetchProducts,
  });

  const { data: customers = [], isLoading: loadingCustomers } = useQuery({
    queryKey: ['customers'],
    queryFn: fetchCustomers,
  });

  const { data: activePromotions = [] } = useQuery({
    queryKey: ['promotions-active'],
    queryFn: fetchActivePromotions,
    staleTime: 2 * 60 * 1000,
  });

  const nonPaymentPromos = useMemo(() => activePromotions.filter(p => p.type !== 'forma_pago'), [activePromotions]);

  const today = new Date().toISOString().split('T')[0];
  const activeProducts = useMemo(() => products.filter(p => p.isActive), [products]);

  // Discounts derived from products — no separate API call needed.
  // Only products that have an active defaultDiscount contribute to this list.
  const discounts = useMemo((): Discount[] => {
    const seen = new Set<number>();
    const result: Discount[] = [];
    activeProducts.forEach(p => {
      const d = p.defaultDiscount;
      if (d?.isActive && !seen.has(d.id)) {
        seen.add(d.id);
        result.push({
          id: d.id, name: d.name, code: d.code, type: d.type,
          value: d.value, maxAmount: d.maxAmount, isActive: d.isActive,
          validFrom: null, validTo: null, isExpired: false, isNotYetValid: false,
        });
      }
    });
    return result;
  }, [activeProducts]);

  const form = useForm<InvoiceForm>({
    resolver: zodResolver(invoiceSchema),
    defaultValues: {
      customerId: 0,
      date: today,
      dueDate: today,
      notes: '',
      items: [{ productId: 0, quantity: 1, unitPrice: 0, taxRate: 0, description: '', discountId: null, discountPct: 0 }],
    },
  });

  const { fields, append, remove } = useFieldArray({ control: form.control, name: 'items' });

  const discountsMap = useMemo(() => {
    const m = new Map<number, Discount>();
    discounts.forEach(d => m.set(d.id, d));
    return m;
  }, [discounts]);

  const watchedCustomerId = form.watch('customerId');

  useEffect(() => {
    if (!watchedCustomerId || watchedCustomerId === 0) {
      setTaxProfiles([]);
      setSelectedTaxProfileId(null);
      setShowNewProfileForm(false);
      return;
    }
    setLoadingProfiles(true);
    fetch(`/api/sales/tax-profiles?alumnoId=${watchedCustomerId}`, { credentials: 'include' })
      .then(r => r.ok ? r.json() : [])
      .then((data: TaxProfile[]) => {
        setTaxProfiles(data);
        const def = data.find(p => p.isDefault) ?? data[0] ?? null;
        setSelectedTaxProfileId(def?.id ?? null);
      })
      .catch(() => setTaxProfiles([]))
      .finally(() => setLoadingProfiles(false));
  }, [watchedCustomerId]);

  const handleSaveNewProfile = async () => {
    if (!watchedCustomerId || watchedCustomerId === 0) return;
    if (!newProfileForm.profileName || !newProfileForm.rfc || !newProfileForm.legalName || !newProfileForm.taxRegimeCode || !newProfileForm.zipCode || !newProfileForm.emailForInvoices) {
      toast({ title: 'Campos requeridos', description: 'Completa todos los campos obligatorios del receptor', variant: 'destructive' });
      return;
    }
    setSavingProfile(true);
    try {
      const r = await fetch(`/api/sales/tax-profiles?alumnoId=${watchedCustomerId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(newProfileForm),
      });
      if (!r.ok) {
        const err = await r.json();
        throw new Error(err.error || 'Error al guardar receptor');
      }
      const created: TaxProfile = await r.json();
      setTaxProfiles(prev => [...prev, created]);
      setSelectedTaxProfileId(created.id);
      setShowNewProfileForm(false);
      setNewProfileForm(emptyNewProfile);
      toast({ title: 'Receptor guardado', description: `${created.profileName} (${created.rfc}) registrado correctamente` });
    } catch (e: any) {
      toast({ title: 'Error', description: e.message, variant: 'destructive' });
    } finally {
      setSavingProfile(false);
    }
  };

  const createMutation = useMutation({
    mutationFn: (params: { data: InvoiceForm; autoIssue: boolean; promoPreview: PromoPreview | null; promoAmountsPerItem: number[]; taxProfileId: number | null }) =>
      createInvoice({ ...params.data, autoIssue: params.autoIssue, promoPreview: params.promoPreview, promoAmountsPerItem: params.promoAmountsPerItem, taxProfileId: params.taxProfileId }),
    onSuccess: async (data) => {
      await queryClient.invalidateQueries({ queryKey: ['sales-invoices'] });
      setCreatedInvoice(data);
    },
    onError: (error: Error) => {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    },
  });

  const buildPromoDiscMap = useCallback((preview: PromoPreview, formItems: any[]) => {
    const map = new Map<number, number>();
    let previewIdx = 0;
    for (let fi = 0; fi < formItems.length; fi++) {
      const item = formItems[fi];
      if (item.productId > 0 && item.quantity > 0 && item.unitPrice >= 0) {
        const pi = preview.items[previewIdx];
        if (pi && pi.promotionDiscountAmount > 0) {
          map.set(fi, pi.promotionDiscountAmount);
        }
        previewIdx++;
      }
    }
    setPromoDiscByFormIdx(map);
  }, []);

  const handlePromoSelect = async (promoId: number | null) => {
    setSelectedPromoId(promoId);
    setPromoPreview(null);
    setPromoDiscByFormIdx(new Map());
    setAutoDetected(false);
    if (promoId === null) {
      setUserClearedPromo(true);
      return;
    }
    setUserClearedPromo(false);
    const currentItems = form.getValues('items') || [];
    const validItems = currentItems.filter((i: any) => i.productId > 0 && i.quantity > 0 && i.unitPrice >= 0);
    if (validItems.length === 0) return;
    setLoadingPreview(true);
    try {
      const preview = await fetchPromotionPreview(promoId, validItems.map((i: any) => ({
        productId: i.productId,
        quantity: i.quantity,
        unitPrice: i.unitPrice,
      })));
      setPromoPreview(preview);
      if (preview) buildPromoDiscMap(preview, currentItems);
    } finally {
      setLoadingPreview(false);
    }
  };

  const watchedItemsForPreview = form.watch('items');
  const itemsKey = JSON.stringify(watchedItemsForPreview?.map((i: any) => ({ p: i.productId, q: i.quantity, u: i.unitPrice })));

  useEffect(() => {
    const currentItems = form.getValues('items') || [];
    const validItems = currentItems.filter((i: any) => i.productId > 0 && i.quantity > 0 && i.unitPrice >= 0);

    if (validItems.length === 0) {
      setPromoPreview(null);
      setPromoDiscByFormIdx(new Map());
      return;
    }

    const itemsPayload = validItems.map((i: any) => ({
      productId: i.productId, quantity: i.quantity, unitPrice: i.unitPrice,
    }));

    if (selectedPromoId) {
      const t = setTimeout(async () => {
        setLoadingPreview(true);
        try {
          const preview = await fetchPromotionPreview(selectedPromoId, itemsPayload);
          setPromoPreview(preview);
          if (preview) buildPromoDiscMap(preview, currentItems);
        } finally {
          setLoadingPreview(false);
        }
      }, 600);
      return () => clearTimeout(t);
    }

    if (!userClearedPromo && nonPaymentPromos.length > 0) {
      const t = setTimeout(async () => {
        setLoadingPreview(true);
        try {
          const previews = await Promise.all(
            nonPaymentPromos.map(p =>
              fetchPromotionPreview(p.id, itemsPayload).catch(() => null)
            )
          );
          let best: PromoPreview | null = null;
          for (const preview of previews) {
            if (preview?.applicable && (best === null || preview.totalDiscount > best.totalDiscount)) {
              best = preview;
            }
          }
          if (best) {
            setSelectedPromoId(best.promotionId);
            setPromoPreview(best);
            setAutoDetected(true);
            buildPromoDiscMap(best, currentItems);
          }
        } finally {
          setLoadingPreview(false);
        }
      }, 700);
      return () => clearTimeout(t);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [itemsKey, selectedPromoId, userClearedPromo]);

  const onSubmit = (data: InvoiceForm, autoIssue = false) => {
    const validItems: InvoiceForm['items'] = [];
    const promoAmountsPerItem: number[] = [];
    data.items.forEach((item, fi) => {
      if (item.productId > 0 && item.quantity > 0) {
        validItems.push(item);
        promoAmountsPerItem.push(promoDiscByFormIdx.get(fi) ?? 0);
      }
    });
    if (validItems.length === 0) {
      toast({ title: 'Error', description: 'Debe agregar al menos un producto con cantidad válida', variant: 'destructive' });
      return;
    }
    const customer = customers.find(c => c.id === data.customerId);
    formSnapshotRef.current = { formData: { ...data, items: validItems }, customer };
    createMutation.mutate({ data: { ...data, items: validItems }, autoIssue, promoPreview, promoAmountsPerItem, taxProfileId: selectedTaxProfileId });
  };

  const printReceipt = () => {
    const snapshot = formSnapshotRef.current;
    if (!snapshot || !createdInvoice) return;
    const { formData, customer } = snapshot;
    const customerName = customer
      ? `${customer.nombre} ${customer.apellidoPaterno} ${customer.apellidoMaterno || ''}`.trim()
      : `Cliente #${formData.customerId}`;

    let subtotal = 0;
    let discountTotalPrint = 0;
    let taxTotal = 0;
    const itemRows = formData.items.map(item => {
      const product = activeProducts.find(p => p.id === item.productId);
      const productName = product ? product.name : (item.description || `Producto #${item.productId}`);
      const base = item.quantity * item.unitPrice;
      const discAmt = computeLineDiscount(item, discounts);
      const lineSubtotal = base - discAmt;
      const lineTax = lineSubtotal * (item.taxRate || 0);
      subtotal += lineSubtotal;
      discountTotalPrint += discAmt;
      taxTotal += lineTax;
      return `
        <tr>
          <td style="padding:6px 8px;border-bottom:1px solid #e5e7eb;">${productName}</td>
          <td style="padding:6px 8px;border-bottom:1px solid #e5e7eb;text-align:right;">${item.quantity}</td>
          <td style="padding:6px 8px;border-bottom:1px solid #e5e7eb;text-align:right;">$${item.unitPrice.toLocaleString('es-MX', { minimumFractionDigits: 2 })}</td>
          ${discAmt > 0 ? `<td style="padding:6px 8px;border-bottom:1px solid #e5e7eb;text-align:right;color:#059669;">−$${discAmt.toLocaleString('es-MX', { minimumFractionDigits: 2 })}</td>` : '<td style="padding:6px 8px;border-bottom:1px solid #e5e7eb;text-align:right;">—</td>'}
          <td style="padding:6px 8px;border-bottom:1px solid #e5e7eb;text-align:right;">${((item.taxRate || 0) * 100).toFixed(0)}%</td>
          <td style="padding:6px 8px;border-bottom:1px solid #e5e7eb;text-align:right;">$${(lineSubtotal + lineTax).toLocaleString('es-MX', { minimumFractionDigits: 2 })}</td>
        </tr>`;
    }).join('');
    const grandTotal = subtotal + taxTotal;
    const notesSection = formData.notes
      ? `<div style="margin-top:16px;padding:12px;background:#f9fafb;border-radius:6px;font-size:13px;color:#374151;"><strong>Notas:</strong> ${formData.notes}</div>`
      : '';
    const companyName = cachedData.company?.legalName || 'GQB';
    const logoUrl = cachedData.company?.logoUrl ? `${window.location.origin}${cachedData.company.logoUrl}` : null;
    const printDate = new Date().toLocaleDateString('es-MX', { year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit' });
    const statusBadgeBg = createdInvoice.status === 'ISSUED' ? '#dcfce7' : '#fef9c3';
    const statusBadgeColor = createdInvoice.status === 'ISSUED' ? '#166534' : '#854d0e';
    const discountRow = discountTotalPrint > 0
      ? `<tr><td>Descuentos:</td><td style="text-align:right;color:#059669;">−$${discountTotalPrint.toLocaleString('es-MX', { minimumFractionDigits: 2 })}</td></tr>`
      : '';

    const html = `<!DOCTYPE html>
<html lang="es">
<head>
  <meta charset="UTF-8"/>
  <title>Recibo ${createdInvoice.invoiceNumber}</title>
  <style>
    * { box-sizing: border-box; margin: 0; padding: 0; }
    body { font-family: Arial, sans-serif; padding: 28px; color: #111; font-size: 14px; }
    .top-bar { display: flex; justify-content: space-between; align-items: center; margin-bottom: 14px; }
    .company-block { display: flex; align-items: center; gap: 12px; }
    .company-logo { max-height: 60px; max-width: 160px; object-fit: contain; }
    .company-name { font-size: 18px; font-weight: bold; color: #111; }
    .print-date { font-size: 12px; color: #6b7280; text-align: right; }
    .header { display: flex; justify-content: space-between; align-items: flex-start; border-top: 2px solid #111; border-bottom: 2px solid #111; padding: 12px 0; margin-bottom: 18px; }
    .title { font-size: 20px; font-weight: bold; margin-bottom: 4px; }
    .folio { color: #6b7280; font-size: 13px; margin-bottom: 6px; }
    .badge { display: inline-block; padding: 2px 10px; border-radius: 12px; font-size: 12px; font-weight: bold; background: ${statusBadgeBg}; color: ${statusBadgeColor}; }
    .meta { text-align: right; font-size: 13px; color: #374151; line-height: 2; }
    .meta strong { color: #111; }
    .info-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; margin-bottom: 20px; font-size: 14px; }
    .info-block { background: #f9fafb; padding: 12px; border-radius: 6px; }
    .info-block label { font-size: 11px; color: #6b7280; text-transform: uppercase; display: block; margin-bottom: 4px; }
    table { width: 100%; border-collapse: collapse; font-size: 13px; }
    th { background: #f3f4f6; padding: 8px; text-align: left; font-size: 12px; color: #374151; }
    th:not(:first-child) { text-align: right; }
    .totals { float: right; margin-top: 16px; font-size: 14px; min-width: 220px; }
    .totals tr td { padding: 4px 8px; }
    .totals tr:last-child td { font-weight: bold; font-size: 16px; border-top: 2px solid #111; padding-top: 8px; }
    @media print { body { padding: 0; } }
  </style>
</head>
<body>
  <div class="top-bar">
    <div class="company-block">
      ${logoUrl ? `<img src="${logoUrl}" alt="${companyName}" class="company-logo"/>` : ''}
      <div class="company-name">${companyName}</div>
    </div>
    <div class="print-date">Impreso el ${printDate}</div>
  </div>
  <div class="header">
    <div>
      <div class="title">Cuenta de Cobro</div>
      <div class="folio">${createdInvoice.invoiceNumber}</div>
      <span class="badge">${createdInvoice.status === 'ISSUED' ? 'Emitida' : 'Borrador'}</span>
    </div>
    <div class="meta">
      <div><strong>Fecha:</strong> ${formData.date}</div>
      ${formData.dueDate ? `<div><strong>Vencimiento:</strong> ${formData.dueDate}</div>` : ''}
    </div>
  </div>
  <div class="info-grid">
    <div class="info-block">
      <label>Cliente</label>
      <strong>${customerName}</strong>
    </div>
  </div>
  <table>
    <thead>
      <tr>
        <th>Descripción</th>
        <th style="text-align:right;">Cant.</th>
        <th style="text-align:right;">Precio Unit.</th>
        <th style="text-align:right;">Descuento</th>
        <th style="text-align:right;">IVA</th>
        <th style="text-align:right;">Total</th>
      </tr>
    </thead>
    <tbody>${itemRows}</tbody>
  </table>
  <table class="totals">
    ${discountRow}
    <tr><td>Subtotal:</td><td style="text-align:right;">$${subtotal.toLocaleString('es-MX', { minimumFractionDigits: 2 })}</td></tr>
    <tr><td>IVA:</td><td style="text-align:right;">$${taxTotal.toLocaleString('es-MX', { minimumFractionDigits: 2 })}</td></tr>
    <tr><td>Total:</td><td style="text-align:right;">$${grandTotal.toLocaleString('es-MX', { minimumFractionDigits: 2 })}</td></tr>
  </table>
  ${notesSection}
  <script>window.onload = () => { window.print(); }<\/script>
</body>
</html>`;

    const win = window.open('', '_blank', 'width=800,height=600');
    if (win) { win.document.write(html); win.document.close(); }
  };

  const handleAddLine = () => {
    append({ productId: 0, quantity: 1, unitPrice: 0, taxRate: 0, description: '', discountId: null, discountPct: 0 });
  };

  const handleProductSelect = useCallback((index: number, productId: number) => {
    const product = activeProducts.find(p => p.id === productId);
    if (product) {
      form.setValue(`items.${index}.productId`, productId);
      form.setValue(`items.${index}.unitPrice`, parseFloat(product.price));
      const taxRateValue = parseFloat(product.taxRate);
      const taxRateDecimal = taxRateValue > 1 ? taxRateValue / 100 : taxRateValue;
      form.setValue(`items.${index}.taxRate`, taxRateDecimal);
      form.setValue(`items.${index}.description`, product.name);
      if (product.defaultDiscountId) {
        form.setValue(`items.${index}.discountId`, product.defaultDiscountId);
        form.setValue(`items.${index}.discountPct`, 0);
      } else {
        form.setValue(`items.${index}.discountId`, null);
      }
    }
  }, [activeProducts, form]);

  const getFilteredProducts = (index: number) => {
    const search = (productSearch[index] || '').toLowerCase();
    if (!search) return activeProducts;
    return activeProducts.filter(
      p => p.sku.toLowerCase().includes(search) || p.name.toLowerCase().includes(search)
    );
  };

  const getFilteredCustomers = () => {
    const search = customerSearch.toLowerCase();
    if (!search) return customers;
    return customers.filter(
      c => c.numeroIdentificacionUnico?.toLowerCase().includes(search) ||
           c.nombre?.toLowerCase().includes(search) ||
           c.apellidoPaterno?.toLowerCase().includes(search) ||
           c.email?.toLowerCase().includes(search)
    );
  };

  const getProductDisplay = (productId: number) => {
    const product = activeProducts.find(p => p.id === productId);
    return product ? `${product.sku} - ${product.name}` : 'Seleccionar producto';
  };

  const getCustomerDisplay = (customerId: number) => {
    const customer = customers.find(c => c.id === customerId);
    return customer
      ? `${customer.numeroIdentificacionUnico} - ${customer.nombre} ${customer.apellidoPaterno || ''}`
      : 'Seleccionar cliente';
  };

  const getDiscountLabel = (discountId: number | null | undefined) => {
    if (!discountId) return 'Sin descuento';
    const d = discountsMap.get(discountId);
    if (!d) return 'Sin descuento';
    return d.type === 'PERCENTAGE'
      ? `${d.name} (${parseFloat(d.value).toFixed(0)}%)`
      : `${d.name} ($${parseFloat(d.value).toLocaleString('es-MX', { minimumFractionDigits: 2 })})`;
  };

  const watchedItems = useWatch({ control: form.control, name: 'items' });

  const hasValidItems = useMemo(() => {
    return (watchedItems || []).some(item => item.productId > 0 && item.quantity > 0);
  }, [watchedItems]);

  const canSubmit = watchedCustomerId > 0 && hasValidItems && !createMutation.isPending;
  const hasDiscounts = discounts.length > 0;

  return (
    <>
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="flex items-center gap-4 mb-6">
          <button
            onClick={() => navigate('/app/sales/invoices')}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ChevronLeft className="w-5 h-5 text-gray-600" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <Receipt className="w-7 h-7 text-emerald-600" />
              Nuevo Recibo
            </h1>
            <p className="text-sm text-gray-500 mt-1">
              Crea una factura para un alumno con productos/servicios
            </p>
          </div>
        </div>

        <form onSubmit={(e) => { e.preventDefault(); }}>
          <div className="bg-white rounded-lg shadow-sm border p-6 mb-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <User className="w-5 h-5 text-gray-400" />
              Datos del Cliente
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-2 md:col-span-2">
                <Label>Cliente (Alumno) *</Label>
                <Controller
                  control={form.control}
                  name="customerId"
                  render={({ field }) => (
                    <Select
                      value={field.value?.toString() || ''}
                      onValueChange={(value) => field.onChange(parseInt(value))}
                    >
                      <SelectTrigger className={`w-full ${form.formState.errors.customerId ? 'border-red-500' : ''}`}>
                        <SelectValue placeholder="Seleccionar cliente">
                          {field.value ? getCustomerDisplay(field.value) : 'Seleccionar cliente'}
                        </SelectValue>
                      </SelectTrigger>
                      <SelectContent>
                        <div className="p-2 sticky top-0 z-10 bg-white">
                          <div className="relative">
                            <Search className="absolute left-2 top-2.5 h-4 w-4 text-gray-400" />
                            <Input
                              placeholder="Buscar por NUI, nombre o email..."
                              className="pl-8"
                              value={customerSearch}
                              onChange={(e) => setCustomerSearch(e.target.value)}
                              onClick={(e) => e.stopPropagation()}
                            />
                          </div>
                        </div>
                        {loadingCustomers ? (
                          <div className="p-4 text-center text-gray-500">
                            <Loader2 className="w-4 h-4 animate-spin mx-auto" />
                          </div>
                        ) : getFilteredCustomers().length === 0 ? (
                          <div className="p-4 text-center text-gray-500 text-sm">No se encontraron clientes</div>
                        ) : (
                          getFilteredCustomers().map((customer) => (
                            <SelectItem key={customer.id} value={customer.id.toString()}>
                              <div className="flex flex-col">
                                <span className="font-medium">
                                  {customer.nombre} {customer.apellidoPaterno} {customer.apellidoMaterno}
                                </span>
                                <span className="text-xs text-gray-500">
                                  NUI: {customer.numeroIdentificacionUnico} {customer.email && `| ${customer.email}`}
                                </span>
                              </div>
                            </SelectItem>
                          ))
                        )}
                      </SelectContent>
                    </Select>
                  )}
                />
                {form.formState.errors.customerId && (
                  <p className="text-xs text-red-500">{form.formState.errors.customerId.message}</p>
                )}
              </div>
            </div>
          </div>

          {/* ── Datos Fiscales del Receptor ─────────────────────────────── */}
          {watchedCustomerId > 0 && (
            <div className="bg-white rounded-lg shadow-sm border p-6 mb-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <Building2 className="w-5 h-5 text-indigo-600" />
                  <h2 className="text-lg font-semibold text-gray-900">Datos Fiscales del Receptor</h2>
                  <span className="text-xs text-gray-400 font-normal">(Para CFDI)</span>
                </div>
                {!showNewProfileForm && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="gap-1.5 text-indigo-600 border-indigo-200 hover:bg-indigo-50"
                    onClick={() => { setShowNewProfileForm(true); setNewProfileForm(emptyNewProfile); }}
                  >
                    <Plus className="w-3.5 h-3.5" />
                    Agregar nuevo receptor
                  </Button>
                )}
              </div>

              {loadingProfiles ? (
                <div className="flex items-center gap-2 text-gray-500 text-sm py-3">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Cargando perfiles fiscales...
                </div>
              ) : taxProfiles.length === 0 && !showNewProfileForm ? (
                <div className="border-2 border-dashed border-gray-200 rounded-lg p-6 text-center">
                  <CreditCard className="w-8 h-8 text-gray-300 mx-auto mb-2" />
                  <p className="text-sm text-gray-500 mb-1">Este cliente no tiene datos fiscales registrados</p>
                  <p className="text-xs text-gray-400">Agrega un receptor para generar CFDI en el futuro</p>
                </div>
              ) : (
                <div className="space-y-2 mb-3">
                  <label className="flex items-start gap-3 p-3 border rounded-lg cursor-pointer hover:bg-gray-50 transition-colors">
                    <input
                      type="radio"
                      name="taxProfile"
                      checked={selectedTaxProfileId === null}
                      onChange={() => setSelectedTaxProfileId(null)}
                      className="mt-0.5 accent-indigo-600"
                    />
                    <div>
                      <p className="text-sm font-medium text-gray-600">Sin datos fiscales</p>
                      <p className="text-xs text-gray-400">No se asociará un receptor a esta cuenta de cobro</p>
                    </div>
                  </label>
                  {taxProfiles.map(profile => (
                    <label
                      key={profile.id}
                      className={`flex items-start gap-3 p-3 border rounded-lg cursor-pointer transition-colors ${selectedTaxProfileId === profile.id ? 'border-indigo-400 bg-indigo-50' : 'hover:bg-gray-50'}`}
                    >
                      <input
                        type="radio"
                        name="taxProfile"
                        checked={selectedTaxProfileId === profile.id}
                        onChange={() => setSelectedTaxProfileId(profile.id)}
                        className="mt-0.5 accent-indigo-600"
                      />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-sm font-semibold text-gray-800">{profile.profileName}</span>
                          {profile.isDefault && (
                            <span className="text-[10px] bg-indigo-100 text-indigo-700 px-1.5 py-0.5 rounded font-medium">Predeterminado</span>
                          )}
                        </div>
                        <p className="text-xs text-gray-600 font-mono mt-0.5">{profile.rfc} — {profile.legalName}</p>
                        <p className="text-xs text-gray-400">Régimen {profile.taxRegimeCode} · CP {profile.zipCode} · {profile.emailForInvoices}</p>
                      </div>
                      {selectedTaxProfileId === profile.id && (
                        <UserCheck className="w-4 h-4 text-indigo-600 shrink-0 mt-0.5" />
                      )}
                    </label>
                  ))}
                </div>
              )}

              {/* Formulario nuevo receptor */}
              {showNewProfileForm && (
                <div className="border border-indigo-200 rounded-lg p-4 bg-indigo-50/40 mt-3">
                  <div className="flex items-center justify-between mb-3">
                    <p className="text-sm font-semibold text-indigo-800">Nuevo Receptor CFDI</p>
                    <button
                      type="button"
                      onClick={() => setShowNewProfileForm(false)}
                      className="p-1 hover:bg-indigo-100 rounded text-indigo-500"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs text-gray-600 mb-1 block">Nombre del perfil *</Label>
                      <Input
                        placeholder='Ej: "Papá", "Empresa XYZ"'
                        value={newProfileForm.profileName}
                        onChange={e => setNewProfileForm(p => ({ ...p, profileName: e.target.value }))}
                        className="text-sm"
                      />
                    </div>
                    <div>
                      <Label className="text-xs text-gray-600 mb-1 block">RFC *</Label>
                      <Input
                        placeholder="XAXX010101000"
                        value={newProfileForm.rfc}
                        onChange={e => setNewProfileForm(p => ({ ...p, rfc: e.target.value.toUpperCase() }))}
                        className="text-sm font-mono"
                        maxLength={13}
                      />
                    </div>
                    <div className="sm:col-span-2">
                      <Label className="text-xs text-gray-600 mb-1 block">Razón Social / Nombre completo *</Label>
                      <Input
                        placeholder="Nombre exacto como aparece en el SAT"
                        value={newProfileForm.legalName}
                        onChange={e => setNewProfileForm(p => ({ ...p, legalName: e.target.value }))}
                        className="text-sm"
                      />
                    </div>
                    <div>
                      <Label className="text-xs text-gray-600 mb-1 block">Régimen Fiscal *</Label>
                      <Select
                        value={newProfileForm.taxRegimeCode}
                        onValueChange={v => setNewProfileForm(p => ({ ...p, taxRegimeCode: v }))}
                      >
                        <SelectTrigger className="text-sm">
                          <SelectValue placeholder="Seleccionar régimen" />
                        </SelectTrigger>
                        <SelectContent>
                          {TAX_REGIMES.map(r => (
                            <SelectItem key={r.code} value={r.code}>
                              <span className="font-mono text-xs mr-2 text-gray-500">{r.code}</span>
                              {r.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-xs text-gray-600 mb-1 block">Código Postal *</Label>
                      <Input
                        placeholder="12345"
                        value={newProfileForm.zipCode}
                        onChange={e => setNewProfileForm(p => ({ ...p, zipCode: e.target.value.replace(/\D/g, '').slice(0, 5) }))}
                        className="text-sm font-mono"
                        maxLength={5}
                      />
                    </div>
                    <div>
                      <Label className="text-xs text-gray-600 mb-1 block">Email para facturas *</Label>
                      <Input
                        type="email"
                        placeholder="cliente@correo.com"
                        value={newProfileForm.emailForInvoices}
                        onChange={e => setNewProfileForm(p => ({ ...p, emailForInvoices: e.target.value }))}
                        className="text-sm"
                      />
                    </div>
                    <div>
                      <Label className="text-xs text-gray-600 mb-1 block">Uso CFDI predeterminado</Label>
                      <Select
                        value={newProfileForm.defaultUsoCfdi}
                        onValueChange={v => setNewProfileForm(p => ({ ...p, defaultUsoCfdi: v }))}
                      >
                        <SelectTrigger className="text-sm">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="D10">D10 — Pagos por servicios educativos</SelectItem>
                          <SelectItem value="G03">G03 — Gastos en general</SelectItem>
                          <SelectItem value="I01">I01 — Construcciones</SelectItem>
                          <SelectItem value="P01">P01 — Por definir</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="sm:col-span-2 flex items-center gap-2">
                      <input
                        type="checkbox"
                        id="isDefaultProfile"
                        checked={newProfileForm.isDefault}
                        onChange={e => setNewProfileForm(p => ({ ...p, isDefault: e.target.checked }))}
                        className="accent-indigo-600"
                      />
                      <Label htmlFor="isDefaultProfile" className="text-xs text-gray-600 cursor-pointer">
                        Marcar como receptor predeterminado
                      </Label>
                    </div>
                  </div>
                  <div className="flex justify-end gap-2 mt-3">
                    <Button type="button" variant="outline" size="sm" onClick={() => setShowNewProfileForm(false)}>
                      Cancelar
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      className="bg-indigo-600 hover:bg-indigo-700 gap-1.5"
                      onClick={handleSaveNewProfile}
                      disabled={savingProfile}
                    >
                      {savingProfile ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <UserCheck className="w-3.5 h-3.5" />}
                      Guardar receptor
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}

          <div className="bg-white rounded-lg shadow-sm border p-6 mb-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <Calendar className="w-5 h-5 text-gray-400" />
              Fechas
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="date">Fecha de Emisión *</Label>
                <Input
                  id="date"
                  type="date"
                  {...form.register('date')}
                  className={form.formState.errors.date ? 'border-red-500' : ''}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="dueDate">Fecha de Vencimiento *</Label>
                <Input
                  id="dueDate"
                  type="date"
                  {...form.register('dueDate')}
                  className={form.formState.errors.dueDate ? 'border-red-500' : ''}
                />
              </div>
            </div>
          </div>

          {nonPaymentPromos.length > 0 && (
            <div className="bg-violet-50 border border-violet-200 rounded-lg p-4 mb-6">
              <div className="flex items-center gap-2 mb-3">
                <Sparkles className="w-4 h-4 text-violet-600" />
                <span className="text-sm font-semibold text-violet-800">Promociones</span>
                {autoDetected && selectedPromoId && !loadingPreview && promoPreview?.applicable && (
                  <span className="text-xs bg-violet-100 text-violet-600 border border-violet-300 rounded-full px-2 py-0.5 font-medium">
                    auto-detectada
                  </span>
                )}
              </div>
              <div className="flex flex-wrap gap-2 mb-3">
                <button
                  type="button"
                  onClick={() => handlePromoSelect(null)}
                  className={`rounded-full px-3 py-1 text-xs border transition-colors ${
                    userClearedPromo || (!selectedPromoId && !loadingPreview)
                      ? 'bg-violet-600 text-white border-violet-600'
                      : 'bg-white text-violet-700 border-violet-200 hover:bg-violet-100'
                  }`}
                >
                  Ninguna
                </button>
                {nonPaymentPromos.map(p => {
                  const discLabel = p.discountType === 'PERCENTAGE'
                    ? `${parseFloat(p.discountValue).toFixed(0)}% dto.`
                    : `$${parseFloat(p.discountValue).toLocaleString('es-MX', { minimumFractionDigits: 2 })} dto.`;
                  const typeIcon = p.type === 'escalonada' ? '📊' : p.type === 'volumen' ? '📦' : '🎁';
                  const isSelected = selectedPromoId === p.id;
                  return (
                    <button
                      key={p.id}
                      type="button"
                      onClick={() => handlePromoSelect(isSelected ? null : p.id)}
                      className={`flex items-center gap-1.5 rounded-full px-3 py-1 text-xs border transition-colors ${
                        isSelected
                          ? 'bg-violet-600 text-white border-violet-600'
                          : 'bg-white text-violet-700 border-violet-200 hover:bg-violet-100'
                      }`}
                    >
                      <span>{typeIcon}</span>
                      <span className="font-medium">{p.name}</span>
                      {p.code && <span className={`font-mono ${isSelected ? 'text-violet-200' : 'text-violet-400'}`}>[{p.code}]</span>}
                      <span className={isSelected ? 'text-violet-200' : 'text-violet-500'}>— {discLabel}</span>
                    </button>
                  );
                })}
              </div>
              {loadingPreview && (
                <div className="flex items-center gap-2 text-xs text-violet-600">
                  <Loader2 className="w-3 h-3 animate-spin" />
                  Detectando promoción aplicable...
                </div>
              )}
              {promoPreview && !loadingPreview && (
                <div className={`rounded-md p-3 text-xs ${promoPreview.applicable ? 'bg-emerald-50 border border-emerald-200' : 'bg-amber-50 border border-amber-200'}`}>
                  {promoPreview.applicable ? (
                    <>
                      <p className="font-semibold text-emerald-800 mb-1">
                        ✅ Promoción aplicada — Ahorro total: ${promoPreview.totalDiscount.toLocaleString('es-MX', { minimumFractionDigits: 2 })}
                      </p>
                      <ul className="space-y-0.5 text-emerald-700">
                        {promoPreview.items.filter(i => i.applicable).map((i, idx) => {
                          const prod = activeProducts.find(p => p.id === i.productId);
                          const label = prod ? `${prod.name}` : `Producto #${i.productId}`;
                          return (
                            <li key={idx}>
                              {label}: −${i.promotionDiscountAmount.toLocaleString('es-MX', { minimumFractionDigits: 2 })}
                            </li>
                          );
                        })}
                      </ul>
                    </>
                  ) : (
                    <p className="text-amber-800">
                      ⚠️ Promoción no aplicable: {promoPreview.reason || 'Los productos actuales no cumplen las condiciones'}
                    </p>
                  )}
                </div>
              )}
            </div>
          )}

          <div className="bg-white rounded-lg shadow-sm border overflow-hidden mb-6">
            <div className="p-4 border-b flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                <FileText className="w-5 h-5 text-gray-400" />
                Detalle de Productos/Servicios
              </h2>
              <Button type="button" variant="outline" size="sm" onClick={handleAddLine}>
                <Plus className="w-4 h-4 mr-1" />
                Agregar Línea
              </Button>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 text-xs uppercase text-gray-500">
                  <tr>
                    <th className="text-left py-3 px-4 font-medium w-[30%]">Producto</th>
                    <th className="text-right py-3 px-4 font-medium w-[9%]">Cantidad</th>
                    <th className="text-right py-3 px-4 font-medium w-[13%]">
                      <div>Precio Unit.</div>
                      <div className="text-[10px] font-normal text-gray-400 normal-case">(base, sin IVA)</div>
                    </th>
                    <th className="text-right py-3 px-4 font-medium w-[8%]">IVA %</th>
                    {hasDiscounts && <th className="text-center py-3 px-4 font-medium w-[20%]">Descuento</th>}
                    <th className="text-right py-3 px-4 font-medium w-[13%]">Total Línea</th>
                    <th className="text-center py-3 px-4 font-medium w-[5%]"></th>
                  </tr>
                </thead>
                <tbody>
                  {fields.map((field, index) => {
                    const item = watchedItems?.[index];
                    const base = (item?.quantity || 0) * (item?.unitPrice || 0);
                    const discAmt = item ? computeLineDiscount(item, discounts) : 0;
                    const promoDiscAmt = promoDiscByFormIdx.get(index) ?? 0;
                    const lineSubtotal = base - discAmt - promoDiscAmt;
                    const lineTotal = lineSubtotal * (1 + (item?.taxRate || 0));

                    const lineProduct = item?.productId
                      ? activeProducts.find(p => p.id === item.productId)
                      : undefined;
                    // Descuento disponible SOLO si el producto tiene uno vinculado y activo.
                    // No se consulta ninguna lista global — viene embebido en el producto.
                    const lineLinkedDiscount = lineProduct?.defaultDiscount?.isActive
                      ? lineProduct.defaultDiscount
                      : null;

                    return (
                      <tr key={field.id} className="border-t hover:bg-gray-50">
                        <td className="py-2 px-4">
                          <Controller
                            control={form.control}
                            name={`items.${index}.productId`}
                            render={({ field: cf }) => (
                              <Select
                                value={cf.value?.toString() || ''}
                                onValueChange={(value) => handleProductSelect(index, parseInt(value))}
                              >
                                <SelectTrigger className="w-full">
                                  <SelectValue placeholder="Seleccionar producto">
                                    {cf.value ? getProductDisplay(cf.value) : 'Seleccionar producto'}
                                  </SelectValue>
                                </SelectTrigger>
                                <SelectContent>
                                  <div className="p-2 sticky top-0 z-10 bg-white">
                                    <div className="relative">
                                      <Search className="absolute left-2 top-2.5 h-4 w-4 text-gray-400" />
                                      <Input
                                        placeholder="Buscar producto..."
                                        className="pl-8"
                                        value={productSearch[index] || ''}
                                        onChange={(e) => setProductSearch(prev => ({ ...prev, [index]: e.target.value }))}
                                        onClick={(e) => e.stopPropagation()}
                                      />
                                    </div>
                                  </div>
                                  {loadingProducts ? (
                                    <div className="p-4 text-center text-gray-500"><Loader2 className="w-4 h-4 animate-spin mx-auto" /></div>
                                  ) : getFilteredProducts(index).length === 0 ? (
                                    <div className="p-4 text-center text-gray-500 text-sm">No se encontraron productos</div>
                                  ) : (
                                    getFilteredProducts(index).map((product) => (
                                      <SelectItem key={product.id} value={product.id.toString()}>
                                        <div className="flex items-center justify-between w-full gap-4">
                                          <div>
                                            <span className="font-mono text-xs mr-2 text-gray-500">{product.sku}</span>
                                            <span>{product.name}</span>
                                          </div>
                                          <div className="text-right shrink-0">
                                            <div className="font-mono text-sm text-emerald-600">
                                              ${(parseFloat(product.price) * (1 + parseFloat(product.taxRate) / 100)).toLocaleString('es-MX', { minimumFractionDigits: 2 })}
                                            </div>
                                            {parseFloat(product.taxRate) > 0 && (
                                              <div className="font-mono text-[10px] text-gray-400">c/IVA</div>
                                            )}
                                          </div>
                                        </div>
                                      </SelectItem>
                                    ))
                                  )}
                                </SelectContent>
                              </Select>
                            )}
                          />
                        </td>
                        <td className="py-2 px-4">
                          <Controller
                            control={form.control}
                            name={`items.${index}.quantity`}
                            render={({ field }) => (
                              <Input
                                type="number" step="0.01" min="0.01" placeholder="1"
                                className="text-right font-mono w-24"
                                value={field.value || ''}
                                onChange={(e) => field.onChange(parseFloat(e.target.value) || 0)}
                              />
                            )}
                          />
                        </td>
                        <td className="py-2 px-4">
                          <Controller
                            control={form.control}
                            name={`items.${index}.unitPrice`}
                            render={({ field }) => (
                              <Input
                                type="number" step="0.01" min="0" placeholder="0.00"
                                className={`text-right font-mono ${watchedItems?.[index]?.productId > 0 ? 'bg-gray-100 cursor-not-allowed text-gray-600' : ''}`}
                                readOnly={watchedItems?.[index]?.productId > 0}
                                value={field.value || ''}
                                onChange={(e) => field.onChange(parseFloat(e.target.value) || 0)}
                              />
                            )}
                          />
                        </td>
                        <td className="py-2 px-4">
                          <Controller
                            control={form.control}
                            name={`items.${index}.taxRate`}
                            render={({ field }) => (
                              <Input
                                type="number" step="0.01" min="0" max="1" placeholder="0.16"
                                className="text-right font-mono w-20"
                                value={field.value || ''}
                                onChange={(e) => field.onChange(parseFloat(e.target.value) || 0)}
                              />
                            )}
                          />
                        </td>
                        {hasDiscounts && (
                          <td className="py-2 px-4">
                            {lineLinkedDiscount ? (
                              <>
                                <Controller
                                  control={form.control}
                                  name={`items.${index}.discountId`}
                                  render={({ field: cf }) => (
                                    <Select
                                      value={cf.value?.toString() ?? 'none'}
                                      onValueChange={(value) => cf.onChange(value === 'none' ? null : parseInt(value))}
                                    >
                                      <SelectTrigger className="w-full text-xs">
                                        <SelectValue>
                                          <span className={cf.value ? 'text-emerald-600 font-medium' : 'text-gray-400'}>
                                            {getDiscountLabel(cf.value)}
                                          </span>
                                        </SelectValue>
                                      </SelectTrigger>
                                      <SelectContent>
                                        <SelectItem value="none">Sin descuento</SelectItem>
                                        <SelectItem value={lineLinkedDiscount.id.toString()}>
                                          <div className="flex items-center justify-between gap-3">
                                            <span>{lineLinkedDiscount.name}</span>
                                            <span className="font-mono text-emerald-600 text-xs font-semibold">
                                              {lineLinkedDiscount.type === 'PERCENTAGE'
                                                ? `${parseFloat(lineLinkedDiscount.value).toFixed(0)}%`
                                                : `$${parseFloat(lineLinkedDiscount.value).toLocaleString('es-MX', { minimumFractionDigits: 2 })}`
                                              }
                                            </span>
                                          </div>
                                        </SelectItem>
                                      </SelectContent>
                                    </Select>
                                  )}
                                />
                                {discAmt > 0 && (
                                  <p className="text-xs text-emerald-600 text-center mt-0.5">
                                    −${discAmt.toLocaleString('es-MX', { minimumFractionDigits: 2 })}
                                  </p>
                                )}
                              </>
                            ) : (
                              <span className="text-xs text-gray-400 block text-center select-none">—</span>
                            )}
                          </td>
                        )}
                        <td className="py-2 px-4 text-right">
                          <span className="font-mono font-medium text-gray-700">
                            ${lineTotal.toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </span>
                          {promoDiscAmt > 0 && (
                            <div className="flex items-center justify-end gap-0.5 mt-0.5">
                              <Sparkles className="w-3 h-3 text-violet-500" />
                              <span className="text-xs text-violet-600 font-mono font-medium">
                                −${promoDiscAmt.toLocaleString('es-MX', { minimumFractionDigits: 2 })}
                              </span>
                            </div>
                          )}
                        </td>
                        <td className="py-2 px-4 text-center">
                          {fields.length > 1 && (
                            <button
                              type="button"
                              onClick={() => remove(index)}
                              className="p-1.5 hover:bg-red-50 rounded-lg transition-colors"
                              title="Eliminar línea"
                            >
                              <Trash2 className="w-4 h-4 text-red-500" />
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <TotalsFooter control={form.control} discounts={discounts} promoDiscByFormIdx={promoDiscByFormIdx} />
          </div>

          <div className="bg-white rounded-lg shadow-sm border p-6 mb-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Notas (Opcional)</h2>
            <Textarea
              placeholder="Notas adicionales para esta cuenta de cobro..."
              {...form.register('notes')}
              rows={3}
            />
          </div>

          <div className="flex items-center justify-end gap-3">
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate('/app/sales/invoices')}
              disabled={createMutation.isPending}
            >
              Cancelar
            </Button>
            <Button
              type="button"
              variant="outline"
              disabled={!canSubmit}
              onClick={form.handleSubmit((data) => onSubmit(data, false))}
              className="min-w-[140px]"
            >
              {createMutation.isPending ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Guardando...</>
              ) : 'Guardar Borrador'}
            </Button>
            <Button
              type="button"
              disabled={!canSubmit}
              onClick={form.handleSubmit((data) => onSubmit(data, true))}
              className="min-w-[160px] bg-emerald-600 hover:bg-emerald-700"
            >
              {createMutation.isPending ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Emitiendo...</>
              ) : (
                <><Receipt className="w-4 h-4 mr-2" />Guardar y Emitir</>
              )}
            </Button>
          </div>

          {!canSubmit && (
            <p className="text-sm text-gray-500 text-right mt-2">
              {!watchedCustomerId || watchedCustomerId === 0
                ? 'Seleccione un cliente para continuar'
                : 'Agregue al menos un producto con cantidad válida'}
            </p>
          )}
        </form>
      </div>
    </div>

    {createdInvoice && (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-8 flex flex-col items-center gap-6">
          <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center">
            <CheckCircle className="w-9 h-9 text-emerald-600" />
          </div>
          <div className="text-center">
            <h2 className="text-xl font-bold text-gray-900 mb-1">
              {createdInvoice.status === 'ISSUED' ? 'Recibo emitido' : 'Borrador guardado'}
            </h2>
            <p className="text-gray-500 text-sm">
              Folio: <span className="font-mono font-semibold text-gray-800">{createdInvoice.invoiceNumber}</span>
            </p>
          </div>
          <div className="flex flex-col sm:flex-row gap-3 w-full">
            <Button variant="outline" className="flex-1" onClick={() => navigate('/app/sales/invoices')}>
              Ver facturas
            </Button>
            <Button className="flex-1 bg-blue-600 hover:bg-blue-700 gap-2" onClick={printReceipt}>
              <Printer className="w-4 h-4" />
              Imprimir recibo
            </Button>
          </div>
        </div>
      </div>
    )}
    </>
  );
}
